import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { useFlag } from '@unleash/proxy-client-react'
import { useQueryClient } from '@tanstack/react-query'
import MessageAlert from 'app/components/MessageAlert'
import { MULTI_ACTION_LOADING } from 'app/constants/subscriberCRM'
import { SUBSCRIBER_GROUPS_QUERY_KEY } from './queries/useSubscriberGroupsQuery'
import { useGroups } from 'commons/useGroups'
import {
  buildBulkAssignPayload,
  validateBeforeAssign,
} from './bulkActionHelpers'

const PAGE_LIMIT = 20

const showSuccessMessage = (dispatch, formatMessage, assignedCount) => {
  if (assignedCount == null) {
    return
  }
  dispatch(
    MessageAlert.showSuccess({
      message: formatMessage(
        { id: 'create.assign.group.dialog.toast.success' },
        { count: assignedCount },
      ),
    }),
  )
}

const useBulkAssignGroupHandlers = ({
  mode,
  subscriberIds = [],
  onAssignGroups,
  selectedTags,
  groups = [],
  params = {},
  onConfirm,
  onCloseDialog,
  onFetchGroups,
  setLoadingState,
  setOpenPanel,
}) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const enableFilterByZipCode = useFlag('mes_5156_display_zip_code_filter')
  const { fetchAllGroups } = useGroups()

  const convertTagsToGroupIds = (tagIds) => {
    const groupIdsSet = new Set()
    groups.forEach((group) => {
      const groupTagIds = group.tags?.map((tag) => tag.id) || []
      const hasSelectedTag = tagIds.some((tagId) => groupTagIds.includes(tagId))
      if (hasSelectedTag) {
        groupIdsSet.add(group.id)
      }
    })
    return Array.from(groupIdsSet)
  }

  const handleBulkAssignToExistingGroups = async () => {
    if (selectedTags.length === 0) return

    if (!validateBeforeAssign(mode, subscriberIds, onAssignGroups)) {
      return
    }

    setLoadingState(MULTI_ACTION_LOADING.ASSIGNING_GROUP)
    try {
      const groupIds = convertTagsToGroupIds(selectedTags)
      const target = buildBulkAssignPayload(
        mode,
        subscriberIds,
        params,
        enableFilterByZipCode,
      )

      const payload = {
        action: 'assign',
        group: {
          create: null,
          existing: {
            groupIds,
            subgroupIds: selectedTags,
          },
        },
        target,
      }

      const response = await onAssignGroups(payload)

      const assignedCount = response?.data?.totalRequested

      showSuccessMessage(dispatch, formatMessage, assignedCount)
      onConfirm?.({ selectedTags })
      onCloseDialog()
    } catch (error) {
      dispatch(
        MessageAlert.showError({
          message: formatMessage({ id: 'toast.bulk.assign.group.error' }),
        }),
      )
    } finally {
      setLoadingState(null)
    }
  }

  const handleCreateAndBulkAssignGroup = async (
    values,
    { resetForm, setFieldError, setSubmitting },
  ) => {
    setLoadingState(MULTI_ACTION_LOADING.CREATING_GROUP)
    setSubmitting(true)

    try {
      const subgroups = values.subGroups
        .map((item) => item.title)
        .filter((title) => title && title.trim() !== '')

      if (subgroups.length === 0) {
        setFieldError(
          'subGroups',
          formatMessage({ id: 'composeDialog.field.required' }),
        )
        return
      }

      if (!validateBeforeAssign(mode, subscriberIds, onAssignGroups)) {
        setSubmitting(false)
        setLoadingState(null)
        return
      }

      const target = buildBulkAssignPayload(
        mode,
        subscriberIds,
        params,
        enableFilterByZipCode,
      )

      const payload = {
        action: 'create_and_assign',
        group: {
          create: {
            name: values.groupName?.trim(),
            subgroups,
          },
          existing: null,
        },
        target,
      }

      const response = await onAssignGroups(payload)
      const assignedCount = response?.data?.assignedCount

      // Reset group list cache in react query
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIBER_GROUPS_QUERY_KEY],
      })

      // Reset group list cache in redux
      await fetchAllGroups()

      onFetchGroups({
        groups: [],
        page: 1,
        limit: PAGE_LIMIT,
        search: '',
      })

      showSuccessMessage(dispatch, formatMessage, assignedCount)

      resetForm()
      setOpenPanel(null)
      onCloseDialog()
    } catch (error) {
      const code = error?.response?.data?.code
      if (code === 608) {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({
              id: 'duplicate.group.name.error.message',
            }),
          }),
        )
        // Set error on groupName field
        setFieldError(
          'groupName',
          formatMessage({
            id: 'duplicate.group.name.error.message',
          }),
        )
      } else {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({ id: 'toast.bulk.assign.group.error' }),
          }),
        )
        setFieldError('groupName', formatMessage({ id: 'message.alert.error' }))
      }
    } finally {
      setLoadingState(null)
      setSubmitting(false)
    }
  }

  return {
    handleBulkAssignToExistingGroups,
    handleCreateAndBulkAssignGroup,
  }
}

export default useBulkAssignGroupHandlers
